import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import BasicModal from '../BasicModal';
import MyAppText from '../MyAppText';
import ErrorMessage from '../ErrorMessage';
import { ClientError } from '../../services/central-api/base-service';
import { FacialCamera, FacialCaptureState } from '../../services/facial-camera';
import { translate } from '../../services/translate';
import { facialLoginService } from '../../services/central-api/facial-login';
import { WindowInformation } from '../../services/window-information';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faClock } from '@fortawesome/free-solid-svg-icons';

const MAX_AUTOMATIC_ATTEMPTS = 10;
const RETRY_DELAY_MS = 2000;
const GUIDANCE_SETTLE_MS = 1500;

interface Props {
    onVerified: () => Promise<void>;
    onCancel: () => Promise<void>;
}

export default function FacialLoginModal({ onVerified, onCancel }: Props) {
    const { styles, theme } = useStyles(stylesheet);
    const { isMobile } = WindowInformation();
    const video = useRef<HTMLVideoElement>(null);
    const guide = useRef<HTMLDivElement>(null);
    const capture = useRef<FacialCamera>();
    const completed = useRef(false);
    const expired = useRef(false);
    const attempts = useRef(0);
    const retryTimer = useRef<ReturnType<typeof setTimeout>>();
    const expiresAt = useRef(facialLoginService.getFacialExpiresAt());
    const callbacks = useRef({ onVerified, onCancel });
    const photoMissing = useRef(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [retrying, setRetrying] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, Math.ceil((expiresAt.current - Date.now()) / 1000)));
    const [state, setState] = useState<FacialCaptureState>({ key: 'facialLoginPreparing', aligned: false, reason: 'none' });
    const [cameraSize, setCameraSize] = useState({ width: 0, height: 0 });
    const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
    const preparing = ['facialLoginPreparing', 'facialLoginCameraPermission', 'facialLoginCameraStarting'].includes(state.key);
    const validating = state.key === 'facialLoginEncoding' || state.key === 'facialLoginWaitingResponse';
    const canRetry = !!error && !retrying && !expired.current && !photoMissing.current && !busy;
    const processing = !expired.current && !photoMissing.current && (busy || retrying || (!error && validating));
    const matching = !expired.current && !photoMissing.current && !error && (busy || state.key === 'facialLoginWaitingResponse');
    const remainingTime = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;
    const previewScale = videoSize.width && videoSize.height
        ? Math.min(cameraSize.width / videoSize.width, cameraSize.height / videoSize.height) : 0;
    const previewWidth = videoSize.width * previewScale;
    const previewHeight = videoSize.height * previewScale;
    const guideWidth = Math.min(previewWidth * 0.82, previewHeight * 0.88 * 0.75) * (isMobile ? 0.8 : 1);

    const ringIdle = 'rgba(239, 243, 250, 0.6)';
    const ringWarn = theme.colors.warnings['300'];
    const ringGood = theme.colors.successes['300'];
    const ringColor = state.reason === 'aligned' ? ringGood : state.reason === 'none' ? ringIdle : ringWarn;
    const ringStrokeWidth = state.reason === 'aligned' ? 2.5 : 1.5;
    const ringDash = state.reason === 'aligned' ? undefined : '4 5';
    const statusColor = state.reason === 'aligned' ? ringGood : state.reason === 'none' ? theme.colors.secondary[400] : ringWarn;
    const transitionStyle = { transition: 'stroke 220ms ease, stroke-width 220ms ease, opacity 220ms ease' } as React.CSSProperties;

    function updateVideoSize() {
        const element = video.current;
        if (element) {
            setVideoSize(previous => previous.width === element.videoWidth && previous.height === element.videoHeight
                ? previous : { width: element.videoWidth, height: element.videoHeight });
        }
    }

    function expireSession() {
        if (completed.current || photoMissing.current) {
            return;
        }
        expired.current = true;
        clearTimeout(retryTimer.current);
        capture.current?.cancel();
        setSecondsLeft(0);
        setRetrying(false);
        setBusy(false);
        setError(translate('facialLoginExpired'));
    }

    useEffect(() => {
        callbacks.current = { onVerified, onCancel };
    }, [onVerified, onCancel]);

    useEffect(() => {
        const element = video.current;
        element?.addEventListener('resize', updateVideoSize);
        return () => element?.removeEventListener('resize', updateVideoSize);
    }, []);

    useEffect(() => {
        if (processing) {
            setShowValidation(true);
            return;
        }
        const timer = setTimeout(() => {
            setShowValidation(false);
        }, GUIDANCE_SETTLE_MS);
        return () => clearTimeout(timer);
    }, [processing]);

    useEffect(() => {
        let mounted = true;
        const camera = new FacialCamera(video.current!, guide.current!, {
            onState: (nextState) => {
                if (mounted && !expired.current && !photoMissing.current) {
                    setState(nextState);
                }
            },
            onError: (err) => {
                if (mounted && !expired.current && !photoMissing.current && !completed.current) {
                    if (err instanceof ClientError && err.message === 'facialLoginPhotoMissing') {
                        photoMissing.current = true;
                        clearTimeout(retryTimer.current);
                        clearTimeout(expirationTimer);
                        clearInterval(countdownTimer);
                        camera.cancel();
                        setBusy(false);
                        setRetrying(false);
                        setError(translate('facialLoginPhotoMissing'));
                        return;
                    }
                    if (Date.now() >= expiresAt.current || (err instanceof ClientError && err.message === 'facialLoginExpired')) {
                        expireSession();
                        return;
                    }
                    setBusy(false);
                    setError(translate(err instanceof ClientError ? err.message : 'facialLoginRequestFailed'));
                    attempts.current += 1;
                    if (attempts.current < MAX_AUTOMATIC_ATTEMPTS && !document.hidden) {
                        setRetrying(true);
                        clearTimeout(retryTimer.current);
                        retryTimer.current = setTimeout(() => {
                            if (!mounted || completed.current || expired.current || photoMissing.current) {
                                return;
                            }
                            if (Date.now() >= expiresAt.current) {
                                expireSession();
                                return;
                            }
                            setRetrying(false);
                            setError('');
                            void camera.start();
                        }, RETRY_DELAY_MS);
                    } else {
                        setRetrying(false);
                    }
                }
            },
            onVerified: async () => {
                if (completed.current || expired.current || photoMissing.current || !mounted) {
                    return;
                }
                if (Date.now() >= expiresAt.current) {
                    expireSession();
                    return;
                }
                completed.current = true;
                clearTimeout(retryTimer.current);
                setBusy(true);
                try {
                    await callbacks.current.onVerified();
                } catch (err) {
                    if (mounted) {
                        completed.current = false;
                        setBusy(false);
                        setError(translate(err instanceof ClientError ? err.message : 'facialLoginRequestFailed'));
                    }
                }
            },
        });
        capture.current = camera;
        const expirationTimer = setTimeout(expireSession, Math.max(0, expiresAt.current - Date.now()));
        const countdownTimer = setInterval(() => {
            if (!completed.current && !expired.current && !photoMissing.current) {
                if (Date.now() >= expiresAt.current) {
                    expireSession();
                } else {
                    setSecondsLeft(Math.ceil((expiresAt.current - Date.now()) / 1000));
                }
            }
        }, 250);
        if (Date.now() >= expiresAt.current) {
            expireSession();
        } else {
            void camera.start();
        }
        function suspend() {
            if (document.hidden && !completed.current && !expired.current && !photoMissing.current) {
                clearTimeout(retryTimer.current);
                setRetrying(false);
                camera.cancel();
                setError(translate('facialLoginInterrupted'));
            }
        }
        function pageHidden() {
            clearTimeout(retryTimer.current);
            camera.cancel();
        }
        document.addEventListener('visibilitychange', suspend);
        window.addEventListener('pagehide', pageHidden);
        return () => {
            mounted = false;
            clearTimeout(retryTimer.current);
            clearTimeout(expirationTimer);
            clearInterval(countdownTimer);
            document.removeEventListener('visibilitychange', suspend);
            window.removeEventListener('pagehide', pageHidden);
            camera.dispose();
        };
    }, []);

    function retry() {
        if (!canRetry || completed.current) {
            return;
        }
        if (Date.now() >= expiresAt.current) {
            expireSession();
            return;
        }
        attempts.current = 0;
        setError('');
        void capture.current?.start();
    }

    async function cancel() {
        if (busy || completed.current) {
            return;
        }
        completed.current = true;
        clearTimeout(retryTimer.current);
        setBusy(true);
        capture.current?.cancel();
        await callbacks.current.onCancel();
    }

    return (
        <BasicModal
            isModalVisible
            setModalVisible={(visible) => {
                if (!visible) {
                    void cancel();
                }
            }}
            width='medium'
            mobileHeight={expired.current || photoMissing.current ? 'fit' : 'fill'}
            animateMobileHeight
            title={translate('facialLoginTitle')}
            secondaryButton={canRetry ? {
                text: translate('facialLoginRetry'),
                onClick: retry,
                disabled: !canRetry,
            } : undefined}
        >
            {photoMissing.current ? <View style={styles.photoMissingPanel} accessibilityRole='alert'>
                <ErrorMessage message={error} />
            </View> : expired.current ? <View style={styles.expiredPanel}>
                <FontAwesomeIcon icon={faClock} fontSize={28} color={theme.colors.textColor} />
                <View style={styles.expiredContent}>
                    <MyAppText style={styles.instructionTitle}>{translate('facialLoginExpiredTitle')}</MyAppText>
                    <MyAppText style={[styles.description, styles.expiredDescription]}>{translate('facialLoginExpired')}</MyAppText>
                </View>
            </View> : <View style={styles.content(isMobile)}>
                <MyAppText style={styles.description}>{translate('facialLoginInstructions')}</MyAppText>
                <View style={styles.sessionInfo}>
                    <MyAppText style={styles.captureState}>
                        {translate(showValidation ? 'facialLoginConfirming' : 'facialLoginAutoCapture')}
                    </MyAppText>
                    <View style={styles.countdown} accessibilityLabel={translate('facialLoginTimeRemaining', { seconds: secondsLeft })}>
                        <FontAwesomeIcon icon={faClock} fontSize={16} color={theme.colors.secondary[400]} />
                        <MyAppText style={[styles.countdownText, secondsLeft <= 10 && styles.timeWarning]}>{remainingTime}</MyAppText>
                    </View>
                </View>
                <View style={styles.camera(isMobile)} onLayout={({ nativeEvent: { layout } }) => {
                    setCameraSize(previous => previous.width === layout.width && previous.height === layout.height
                        ? previous : { width: layout.width, height: layout.height });
                }}>
                    <View style={[styles.preview, { width: previewWidth || '100%', height: previewHeight || '100%' }]}>
                        <video ref={video} autoPlay playsInline muted onLoadedMetadata={updateVideoSize} style={{
                            position: 'absolute', inset: 0, width: '100%', height: '100%',
                            objectFit: 'contain', transform: 'scaleX(-1)',
                        }} />
                        {preparing && <div style={{
                            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: theme.colors.backgrounds['BG-1'],
                        }}>
                            <FontAwesomeIcon icon={faCamera} fontSize={36} color={theme.colors.neutral[400]} />
                        </div>}
                        {/* soft radial dim outside the oval */}
                        <div aria-hidden style={{
                            position: 'absolute', inset: 0, pointerEvents: 'none', display: previewScale > 0 ? 'block' : 'none',
                            background: 'radial-gradient(ellipse 45% 47% at 50% 50%, transparent 60%, rgba(10,12,24,0.5) 90%)',
                        }} />
                        <div ref={guide} aria-hidden style={{
                            position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                            width: guideWidth || 1, height: guideWidth / 0.75 || 1, display: previewScale > 0 ? 'block' : 'none',
                            boxSizing: 'border-box', pointerEvents: 'none',
                        }}>
                            <svg viewBox='0 0 100 125' preserveAspectRatio='xMidYMid meet' width='100%' height='100%' aria-hidden>
                                <ellipse
                                    cx='50' cy='62.5' rx='45' ry='58.75'
                                    fill='none' stroke={ringColor}
                                    strokeWidth={ringStrokeWidth}
                                    strokeDasharray={ringDash}
                                    style={transitionStyle}
                                />
                                <path d='M 27.5 11.6 A 45 58.75 0 0 1 72.5 11.6'
                                    fill='none' stroke={ringWarn} strokeWidth={3.5} strokeLinecap='round'
                                    opacity={state.direction === 'top' ? 1 : 0} style={transitionStyle} />
                                <path d='M 89 33.1 A 45 58.75 0 0 1 89 91.9'
                                    fill='none' stroke={ringWarn} strokeWidth={3.5} strokeLinecap='round'
                                    opacity={state.direction === 'right' ? 1 : 0} style={transitionStyle} />
                                <path d='M 72.5 113.4 A 45 58.75 0 0 1 27.5 113.4'
                                    fill='none' stroke={ringWarn} strokeWidth={3.5} strokeLinecap='round'
                                    opacity={state.direction === 'bottom' ? 1 : 0} style={transitionStyle} />
                                <path d='M 11 91.9 A 45 58.75 0 0 1 11 33.1'
                                    fill='none' stroke={ringWarn} strokeWidth={3.5} strokeLinecap='round'
                                    opacity={state.direction === 'left' ? 1 : 0} style={transitionStyle} />
                            </svg>
                        </div>
                        {/* status caption inside the preview */}
                        {!preparing && !matching && !canRetry && previewScale > 0 && <View style={styles.captionWrap} pointerEvents='none'>
                            <MyAppText style={[styles.caption, { color: statusColor }]}>{translate(state.key)}</MyAppText>
                        </View>}
                        {/* loading overlay while waiting for backend match */}
                        <div role='status' aria-live='polite' aria-hidden={!matching} style={{
                            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 12, padding: 20,
                            boxSizing: 'border-box', backgroundColor: '#000000A6',
                            opacity: matching ? 1 : 0, visibility: matching ? 'visible' : 'hidden',
                            transition: 'opacity 200ms ease', pointerEvents: 'none',
                        }}>
                            {matching && <>
                                <ActivityIndicator size='large' color='#FFFFFF' />
                                <MyAppText style={styles.validatingText}>{translate('facialLoginConfirming')}</MyAppText>
                            </>}
                        </div>
                        {/* retry notice overlay */}
                        <div role='status' aria-live='polite' aria-hidden={!canRetry} style={{
                            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 20, boxSizing: 'border-box', backgroundColor: '#000000A6',
                            opacity: canRetry ? 1 : 0, visibility: canRetry ? 'visible' : 'hidden',
                            transition: 'opacity 200ms ease', pointerEvents: 'none',
                        }}>
                            {canRetry && <MyAppText style={styles.retryNotice}>{translate('facialLoginRetryNotice')}</MyAppText>}
                        </div>
                    </View>
                </View>
                <View style={styles.tips}>
                    <MyAppText style={styles.tipsTitle}>{translate('facialLoginTipsTitle')}</MyAppText>
                    {['facialLoginKeepStill', 'facialLoginDistanceTip', 'facialLoginLightingTip'].map(key => <View key={key} style={styles.tip}>
                        <MyAppText style={styles.bullet} aria-hidden>•</MyAppText>
                        <MyAppText style={styles.tipText}>{translate(key)}</MyAppText>
                    </View>)}
                </View>
            </View>}
        </BasicModal>
    );
}

const stylesheet = createStyleSheet(theme => ({
    content: (isMobile: boolean) => ({
        gap: 16,
        flex: isMobile ? 1 : undefined,
        minHeight: isMobile ? 500 : undefined,
    }),
    instructionTitle: {
        fontSize: 16,
        lineHeight: 24,
        fontWeight: '600',
        textAlign: 'center',
        color: theme.colors.textColor,
    },
    description: {
        flexShrink: 0,
        fontSize: 16,
        lineHeight: 24,
        color: theme.colors.secondary[400],
    },
    sessionInfo: {
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: 32,
    },
    captureState: {
        flex: 1,
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.secondary[400],
    },
    countdown: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        minHeight: 28,
    },
    countdownText: {
        fontSize: 16,
        lineHeight: 24,
        fontWeight: '600',
        fontVariant: ['tabular-nums'],
        color: theme.colors.textColor,
    },
    timeWarning: {
        color: theme.colors.alert[300],
    },
    camera: (isMobile: boolean) => ({
        height: isMobile ? undefined : 360,
        flex: isMobile ? 1 : undefined,
        minHeight: isMobile ? 180 : undefined,
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    }),
    preview: {
        flexShrink: 0,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: theme.colors.backgrounds['BG-1'],
    },
    captionWrap: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 12,
        alignItems: 'center',
        paddingHorizontal: 12,
    },
    caption: {
        fontSize: 14,
        lineHeight: 20,
        fontWeight: '600',
        textAlign: 'center',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
        backgroundColor: 'rgba(10,12,24,0.55)',
    },
    validatingText: {
        fontSize: 16,
        lineHeight: 24,
        fontWeight: '600',
        color: '#FFFFFF',
        textAlign: 'center',
    },
    tips: {
        flexShrink: 0,
        gap: 8,
    },
    retryNotice: {
        fontSize: 16,
        lineHeight: 24,
        fontWeight: '600',
        textAlign: 'center',
        color: '#FFFFFF',
    },
    tipsTitle: {
        fontSize: 14,
        lineHeight: 20,
        fontWeight: '600',
        color: theme.colors.textColor,
    },
    tip: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
    },
    bullet: {
        width: 12,
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.secondary[400],
    },
    tipText: {
        flex: 1,
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.secondary[400],
    },
    photoMissingPanel: {
        paddingVertical: 16,
    },
    expiredPanel: {
        alignItems: 'center',
        paddingVertical: 24,
        gap: 12,
    },
    expiredContent: {
        width: '100%',
        alignItems: 'center',
        gap: 8,
    },
    expiredDescription: {
        textAlign: 'center',
    },
}));
